import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { GET_RISK } from "./graphql";
import { Card, Kpi, ReportState, SortTable, PersonButton, BookingLink, useReport, fmt, rupees } from "./ui";

// Bookings that look wrong and how often each professional shows up.

const FLAG_CLS = {
  MOCK_GPS: "bg-red-100 text-red-800",
  FAR: "bg-red-50 text-red-700",
  SHORT: "bg-amber-50 text-amber-800",
  BURST: "bg-amber-50 text-amber-800",
  ADDON_NO_OTP: "bg-violet-50 text-violet-700",
  FORCE_SCAN: "bg-gray-100 text-gray-700",
  CANCEL_BY_PRO: "bg-sky-50 text-sky-700",
};

const RiskTab = ({ filter, onPick, onOpenBooking }) => {
  const { data, loading, error } = useReport(GET_RISK, "getRiskReport", filter);
  const [flag, setFlag] = useState(null);
  const label = useMemo(() => Object.fromEntries((data?.flags || []).map((f) => [f.key, f])), [data]);
  const Pill = ({ k }) => (
    <span title={label[k]?.hint} className={`px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap ${FLAG_CLS[k] || "bg-gray-100 text-gray-700"}`}>
      {label[k]?.label || k}
    </span>
  );

  const empColumns = useMemo(
    () => [
      { key: "riskPer10", label: "Risk", title: "Weighted flags per 10 bookings", cell: (e) => <span className={e.riskPer10 >= 10 ? "text-red-600 font-semibold" : e.riskPer10 >= 4 ? "text-amber-600 font-medium" : "text-gray-700"}>{fmt(e.riskPer10, 1)}</span> },
      { key: "flagged", label: "Flagged", cell: (e) => `${e.flagged}/${e.bookings}`, sort: (e) => (e.bookings ? e.flagged / e.bookings : 0) },
      ...(data?.flags || []).map((f) => ({
        key: f.key,
        label: f.label,
        title: f.hint,
        cell: (e) => (e.counts[f.key] ? <span className="text-gray-900">{e.counts[f.key]}</span> : <span className="text-gray-300">·</span>),
        sort: (e) => e.counts[f.key] || 0,
      })),
      {
        key: "cashShare",
        label: "Cash share",
        title: `Completed bookings paid in cash. Flagged when ${data?.thresholds?.cashGapPoints ?? 25}+ points above the team (${data?.team?.cashShare ?? "—"}%)`,
        cell: (e) => (e.cashShare === null || e.cashShare === undefined ? "—" : <span className={e.cashHeavy ? "text-red-600 font-medium" : "text-gray-700"}>{e.cashShare}%{e.cashHeavy ? " ⚑" : ""}</span>),
      },
    ],
    [data]
  );

  const rows = useMemo(() => (data?.bookings || []).filter((b) => !flag || b.flags.includes(flag)), [data, flag]);
  const bookingColumns = [
    { key: "bookingDate", label: "Date", cell: (r) => `${dayjs(r.bookingDate).format("D MMM")} ${r.timeSlot || ""}`, sort: (r) => r.bookingDate },
    { key: "bookingId", label: "Booking", cell: (r) => <BookingLink row={r} onOpen={onOpenBooking} />, sort: (r) => r.bookingId },
    { key: "name", label: "Professional", cell: (r) => <PersonButton row={r} onPick={onPick} />, sort: (r) => r.name || "" },
    { key: "flags", label: "Why", cell: (r) => <span className="flex flex-wrap gap-1">{r.flags.map((f) => <Pill key={f} k={f} />)}</span>, sort: (r) => r.score },
    { key: "detail", label: "Detail", cell: (r) => <span className="text-xs text-gray-500">{[r.distanceM !== null && `${fmt(r.distanceM)} m away`, r.actual !== null && r.expected ? `${r.actual}/${r.expected} min` : null].filter(Boolean).join(" · ") || "—"}</span>, sort: (r) => r.distanceM || 0 },
    { key: "price", label: "Price", cell: (r) => rupees(r.price) },
    { key: "status", label: "Status", cell: (r) => <span className="text-gray-600">{r.status}</span> },
  ];

  return (
    <ReportState loading={loading} error={error} data={data}>
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Flagged bookings" value={fmt(data.team.flaggedBookings)} valueClass={data.team.flaggedBookings ? "text-amber-600" : "text-gray-900"} sub={`of ${fmt(data.team.bookings)} in the period`} />
            <Kpi label="Fake GPS" value={fmt(data.team.counts.MOCK_GPS || 0)} valueClass={data.team.counts.MOCK_GPS ? "text-red-600" : "text-gray-900"} sub="bookings with a mocked location" />
            <Kpi label="Not at customer" value={fmt(data.team.counts.FAR || 0)} valueClass={data.team.counts.FAR ? "text-red-600" : "text-gray-900"} sub={`over ${data.thresholds.farMetres} m at Reached and Start`} />
            <Kpi label="Cash-heavy pros" value={fmt(data.team.cashHeavy)} valueClass={data.team.cashHeavy ? "text-red-600" : "text-gray-900"} sub={`team cash share ${data.team.cashShare ?? "—"}%`} />
          </div>

          <Card title="By professional" hint="Each flag is a reason to look, not proof. Risk weighs serious flags more (fake GPS 5, not at customer 3, cash-heavy 3, short/burst/add-on 2, others 1) per 10 bookings.">
            <SortTable
              columns={empColumns}
              rows={data.employees}
              initialSort={{ key: "riskPer10", dir: -1 }}
              rowKey={(e) => e.employeeId}
              nameCell={{ label: "Professional", cell: (e) => <PersonButton row={e} onPick={onPick} /> }}
            />
          </Card>

          <Card
            title="Flagged bookings"
            hint={`${fmt(rows.length)} shown${data.bookingsTotal > data.bookings.length ? ` (the ${data.bookings.length} most serious of ${fmt(data.bookingsTotal)})` : ""}. Click a booking id to open it.`}
            right={
              <div className="flex flex-wrap gap-1.5">
                {data.flags.map((f) =>
                  data.team.counts[f.key] ? (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFlag(flag === f.key ? null : f.key)}
                      className={`px-2.5 py-1 rounded-full text-xs border ${flag === f.key ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
                    >
                      {f.label} · {data.team.counts[f.key]}
                    </button>
                  ) : null
                )}
              </div>
            }
          >
            <SortTable columns={bookingColumns} rows={rows} initialSort={{ key: "flags", dir: -1 }} rowKey={(r) => r.orderId} empty="No flagged bookings." />
          </Card>
        </>
      )}
    </ReportState>
  );
};

export default RiskTab;
